import React, { useEffect, useState, useRef } from "react";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, UploadCloud, Plus, Loader2, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CAN_IMPORT = ["super_admin", "accounts"];

export default function Royalties() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const canImport = CAN_IMPORT.includes(user.role);
  const isAdmin = user.role === "super_admin";
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [manual, setManual] = useState({ period: "", dsp: "", artist: "", label: "", release: "", track: "", streams: 0, net_revenue: 0, payable_amount: 0, currency: "USD" });

  const load = () => {
    api.get("/royalties", { params: search ? { search } : {} }).then((r) => setRows(r.data));
    api.get("/royalties/payouts").then((r) => setPayouts(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(() => { api.get("/royalties", { params: search ? { search } : {} }).then((r) => setRows(r.data)); }, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search]);

  const approveAll = async () => {
    const { data } = await api.post("/royalties/approve", { ids: [] });
    toast.success(`Approved ${data.approved} records`);
    load();
  };
  const delRoyalty = async (rid) => {
    if (!window.confirm("Delete this royalty entry permanently?")) return;
    await api.delete(`/royalties/${rid}`);
    toast.success("Deleted");
    load();
  };

  const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const downloadTemplate = () => {
    window.open(`${API}/royalties/template?auth=${getToken()}`, "_blank");
  };

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/royalties/import/preview", fd);
      setPreview(data);
      if (data.missing_columns?.length) toast.error("Missing columns: " + data.missing_columns.join(", "));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse file");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commitImport = async () => {
    const rows = preview.valid.map((v) => v.data);
    setBusy(true);
    try {
      const { data } = await api.post("/royalties/import", { rows });
      toast.success(`Imported ${data.inserted} records`);
      setPreview(null);
      load();
    } catch (e) {
      toast.error("Import failed");
    } finally {
      setBusy(false);
    }
  };

  const saveManual = async () => {
    try {
      await api.post("/royalties", {
        ...manual,
        streams: Number(manual.streams), net_revenue: Number(manual.net_revenue), payable_amount: Number(manual.payable_amount),
      });
      toast.success("Royalty record added");
      setManualOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="royalties-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Royalties</h1>
          <p className="mt-1 text-muted-foreground">{canImport ? "Import and manage royalty records." : "Read-only royalty breakdown."}</p>
        </div>
      </div>

      <Tabs defaultValue="view">
        <TabsList data-testid="royalty-tabs">
          <TabsTrigger value="view" data-testid="tab-view">Royalty View</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payout Summary</TabsTrigger>
          {canImport && <TabsTrigger value="import" data-testid="tab-import">Import Royalty</TabsTrigger>}
        </TabsList>

        <TabsContent value="view" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Filter by track, release or ISRC…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="royalty-search-input"
              className="max-w-sm"
            />
            {search && <Button variant="ghost" size="sm" onClick={() => setSearch("")} data-testid="royalty-search-clear">Clear</Button>}
          </div>
          {canImport && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{rows.filter((r) => r.status === "pending").length} pending Super Admin approval</p>
              {isAdmin && rows.some((r) => r.status === "pending") && (
                <Button size="sm" onClick={approveAll} data-testid="approve-royalties-btn">Approve All Pending</Button>
              )}
            </div>
          )}
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead><TableHead>Release</TableHead><TableHead>Track</TableHead>
                  <TableHead>DSP</TableHead><TableHead className="text-right">Streams</TableHead>
                  <TableHead className="text-right">Net Revenue</TableHead><TableHead className="text-right">Payable</TableHead>
                  {canImport && <TableHead>Status</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">No royalty records.</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`royalty-row-${r.id}`}>
                    <TableCell>{r.period}</TableCell>
                    <TableCell>{r.release || "—"}</TableCell>
                    <TableCell>{r.track || "—"}</TableCell>
                    <TableCell>{r.dsp}</TableCell>
                    <TableCell className="text-right font-mono">{Number(r.streams || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">{money(r.net_revenue)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-emerald-400">{money(r.payable_amount)}</TableCell>
                    {canImport && <TableCell>
                      {r.status === "pending"
                        ? <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-400">Pending</Badge>
                        : <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">Approved</Badge>}
                      {isAdmin && <Button variant="ghost" size="icon" className="ml-2 h-7 w-7" onClick={() => delRoyalty(r.id)} data-testid={`del-royalty-${r.id}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>}
                    </TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="payouts" className="mt-4">
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Artist</TableHead><TableHead>Period</TableHead><TableHead className="text-right">Streams</TableHead><TableHead className="text-right">Net Revenue</TableHead><TableHead className="text-right">Total Payable</TableHead><TableHead className="text-right">Records</TableHead></TableRow></TableHeader>
              <TableBody>
                {payouts.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No payout data.</TableCell></TableRow>}
                {payouts.map((p, i) => (
                  <TableRow key={i} data-testid={`payout-row-${i}`}>
                    <TableCell className="font-medium">{p.artist}</TableCell>
                    <TableCell>{p.period}</TableCell>
                    <TableCell className="text-right font-mono">{Number(p.streams).toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">{money(p.net)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-emerald-400">{money(p.payable)}</TableCell>
                    <TableCell className="text-right">{p.records}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {canImport && (
          <TabsContent value="import" className="mt-4 space-y-4">
            <Card className="flex flex-wrap items-center justify-between gap-4 p-6">
              <div>
                <h3 className="font-heading text-lg font-semibold">Import Royalty Excel / CSV</h3>
                <p className="text-sm text-muted-foreground">Upload a file matching the template columns. Invalid rows are shown and skipped.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="mr-2 h-4 w-4" />Template</Button>
                <Button onClick={() => setManualOpen(true)} variant="outline" data-testid="manual-entry-btn"><Plus className="mr-2 h-4 w-4" />Manual Entry</Button>
                <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-royalty-btn">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}Upload File
                </Button>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="royalty-file-input" />
              </div>
            </Card>

            {preview && (
              <Card className="p-6" data-testid="import-preview">
                <div className="mb-4 flex flex-wrap gap-4">
                  <span className="flex items-center gap-2 text-sm text-emerald-400"><CheckCircle2 className="h-4 w-4" />{preview.valid_count} valid</span>
                  <span className="flex items-center gap-2 text-sm text-rose-400"><AlertTriangle className="h-4 w-4" />{preview.error_count} errors</span>
                </div>
                {preview.error_count > 0 && (
                  <div className="mb-4 max-h-40 overflow-y-auto rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm" data-testid="import-errors">
                    {preview.errors.map((e, i) => (
                      <p key={i} className="text-rose-300">Row {e.row}: {e.errors.join("; ")}</p>
                    ))}
                  </div>
                )}
                {preview.valid_count > 0 && (
                  <>
                    <div className="max-h-64 overflow-auto rounded-lg border border-border">
                      <Table>
                        <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>DSP</TableHead><TableHead>Artist</TableHead><TableHead>Track</TableHead><TableHead className="text-right">Payable</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {preview.valid.slice(0, 50).map((v, i) => (
                            <TableRow key={i}>
                              <TableCell>{v.data["Royalty Period"]}</TableCell>
                              <TableCell>{v.data["DSP"]}</TableCell>
                              <TableCell>{v.data["Artist"]}</TableCell>
                              <TableCell>{v.data["Track"]}</TableCell>
                              <TableCell className="text-right font-mono">{money(v.data["Payable Amount"])}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => setPreview(null)}>Cancel</Button>
                      <Button onClick={commitImport} disabled={busy} data-testid="confirm-import-btn">Import {preview.valid_count} Records</Button>
                    </div>
                  </>
                )}
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Manual Royalty Entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[["period", "Period"], ["dsp", "DSP"], ["artist", "Artist"], ["label", "Label"], ["release", "Release"], ["track", "Track"], ["currency", "Currency"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs">{l}</Label><Input value={manual[k]} onChange={(e) => setManual({ ...manual, [k]: e.target.value })} data-testid={`manual-${k}`} className="mt-1" /></div>
            ))}
            {[["streams", "Streams"], ["net_revenue", "Net Revenue"], ["payable_amount", "Payable Amount"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs">{l}</Label><Input type="number" value={manual[k]} onChange={(e) => setManual({ ...manual, [k]: e.target.value })} data-testid={`manual-${k}`} className="mt-1" /></div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button onClick={saveManual} data-testid="save-manual-btn">Save Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
